import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useState } from "react";
import { Ticket, Home, Calendar, LayoutDashboard, LogOut, Menu, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { loginUrl } from "@/auth/loginUrl";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const currentPageName = location.pathname.replace(/^\//, "") || "Home";

  const navItems = [
    { name: "Home", page: "Home", icon: Home },
    { name: "Events", page: "Events", icon: Calendar },
    { name: "My Tickets", page: "MyTickets", icon: Ticket },
    ...(user?.role === "admin" ? [{ name: "Dashboard", page: "AdminDashboard", icon: LayoutDashboard }] : []),
  ];

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate(createPageUrl("Home"));
  };

  const signIn = () => {
    setMenuOpen(false);
    navigate(loginUrl());
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to={createPageUrl("Home")} className="flex items-center gap-2">
              <div className="bg-indigo-600 rounded-lg p-1.5">
                <Ticket className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">EventPass</span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map(({ name, page, icon: Icon }) => (
                <Link
                  key={page}
                  to={createPageUrl(page)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPageName === page
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {name}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <div className="flex items-center gap-3">
                  <Link to={createPageUrl("UserProfile")}>
                    <span className="text-sm text-gray-600 hover:text-indigo-600 cursor-pointer truncate max-w-[160px] transition-colors">
                      {user.display_name || user.email}
                    </span>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-gray-600" title="Sign out">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button size="sm" onClick={signIn} className="bg-indigo-600 hover:bg-indigo-700">
                  Sign In
                </Button>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            {navItems.map(({ name, page, icon: Icon }) => (
              <Link
                key={page}
                to={createPageUrl(page)}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  currentPageName === page ? "bg-indigo-50 text-indigo-600" : "text-gray-600"
                }`}
              >
                <Icon className="h-4 w-4" />
                {name}
              </Link>
            ))}
            {user && (
              <Link
                to={createPageUrl("UserProfile")}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 w-full"
              >
                <User className="h-4 w-4" /> My Profile
              </Link>
            )}
            {user ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 w-full"
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            ) : (
              <button
                onClick={signIn}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 w-full"
              >
                Sign In
              </button>
            )}
          </div>
        )}
      </nav>

      <main className="pt-16 print:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
